<script setup lang="ts">
import type MuxVideoElement from '@mux/mux-video'
import { useDocumentVisibility } from '@vueuse/core'

if (import.meta.client) {
  await import('@mux/mux-video')
}

const props = withDefaults(
  defineProps<{
    media?: VideoProps
    controls?: boolean
    loop?: boolean
    autoplay?: boolean
    muted?: boolean
    aspect?: number | [number?, number?]
    fill?: 'h' | 'w' | 'both'
  }>(),
  {
    media: undefined,
    aspect: 16 / 9,
    fill: 'both',
    controls: false,
    loop: true,
    autoplay: true,
    muted: true,
  },
)

const $muxPlayer = ref<MuxVideoElement | null>(null)
const $el = ref<HTMLElement | null>(null)

const playing = ref(false)
const isReady = ref(false)
const loaded = ref(false)

// ✅ SAFE PLAY FUNCTION (CRITICAL FIX)
const tryPlay = async () => {
  if (!$muxPlayer.value) return

  try {
    await $muxPlayer.value.play()
    playing.value = true
  } catch (err) {
    console.warn('Autoplay blocked')

    // fallback → show controls so user can play
    playing.value = false
    $muxPlayer.value.controls = true
  }
}

// ✅ FIX: trigger when video is ready
watch($muxPlayer, () => {
  if (!$muxPlayer.value) return

  // 🔥 use loadeddata instead of canplaythrough
  $muxPlayer.value.addEventListener('loadeddata', () => {
    loaded.value = true
    isReady.value = true
    tryPlay()
  })
})

// Intersection Observer (KEEP but FIX behavior)
let observer: IntersectionObserver | null = null

onMounted(() => {
  if ($el.value) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!$muxPlayer.value) return

          if (entry.isIntersecting) {
            tryPlay()
          } else {
            $muxPlayer.value.pause()
            playing.value = false
          }
        })
      },
      {
        rootMargin: '800px 0px',
        threshold: 0.25,
      },
    )

    observer.observe($el.value)
  }
})

onUnmounted(() => {
  observer?.disconnect()
})

// ✅ FIX visibility bug
const visibility = useDocumentVisibility()

watch(visibility, () => {
  if (visibility.value === 'visible' && $muxPlayer.value && $muxPlayer.value.paused) {
    tryPlay()
  }
})

// aspect CSS
const aspectCSS = computed(() => {
  if (!props.aspect) return

  if (typeof props.aspect === 'number') {
    return {
      '--aspect-mobile': `${props.aspect}/1`,
      '--aspect-desktop': `${props.aspect}/1`,
    }
  }

  return {
    '--aspect-mobile': `${props.aspect[0] || 16 / 9}/1`,
    '--aspect-desktop': `${props.aspect[1] || 16 / 9}/1`,
  }
})

const emit = defineEmits(['loaded'])
</script>

<template>
  <div
    v-if="media"
    ref="$el"
    class="relative"
    :class="{
      'h-full w-full': fill === 'both',
      'h-full w-auto': fill === 'h',
      'h-auto w-full': fill === 'w',
    }"
  >
    <!-- Thumbnail -->
    <img
      v-if="media.asset.playbackId"
      class="absolute inset-0 z-0 h-full w-full object-cover"
      :src="`https://image.mux.com/${media.asset.playbackId}/thumbnail.webp?width=800&height=450&fit_mode=smartcrop&time=${media?.asset?.thumbTime || 1}`"
      alt=""
      loading="lazy"
    />

    <!-- Play Button (fallback) -->
    <button v-if="!playing" class="absolute inset-0 z-10 grid place-items-center" @click="tryPlay">
      <div class="bg-black/60 px-4 py-2 text-white"><IconsPlay class="size-5 lg:size-7" /></div>
    </button>

    <mux-video
      ref="$muxPlayer"
      class="relative z-0 transition-opacity duration-500"
      :style="{
        ...aspectCSS,
        '--controls': !controls ? 'none' : '',
        '--media-object-fit': 'cover',
      }"
      playsinline
      :playback-id="media.asset.playbackId"
      :loop="loop"
      :muted="muted"
      preload="auto"
      :controls="controls || !playing"
      :class="[
        fill === 'h' ? 'h-full' : '',
        fill === 'w' ? 'w-full' : '',
        fill === 'both' ? 'h-full w-full' : '',
        aspect ? 'aspect-(--aspect-mobile) lg:aspect-(--aspect-desktop)' : 'aspect-video',
        loaded ? 'opacity-100' : 'opacity-0',
      ]"
      @loadedmetadata="emit('loaded')"
    />
  </div>
</template>

<style lang="postcss">
mux-video {
  --media-object-fit: cover;

  video {
    height: 100%;
    width: 100%;
  }
}
</style>
